const { MODULE_FREE_LIMIT_SETTING, MODULE_LIMIT_SETTING } = require('../constants/settings');
const { LicenseInterval, Module } = require('../prisma/types');
const { dayjs } = require('./date');
const { numberFormat } = require('./formatter');

function hasLicenseLimit(limit) {
  return limit != null && limit >= 0;
}

function formatLicenseLimit(limit, opts, locale) {
  if (limit == null) {
    return 'N/A';
  }

  if (limit === -1) {
    return 'Unlimited';
  }

  return numberFormat(limit, opts, locale);
}

function reachedLicenseLimit(value, limit) {
  return hasLicenseLimit(limit) && value >= limit;
}

function exceededLicenseLimit(value, limit) {
  return hasLicenseLimit(limit) && value > limit;
}

function isModuleFree(module) {
  return module?.level === 1;
}

function isLicenseFree(dataSourceLicense) {
  if (dataSourceLicense) {
    if (dataSourceLicense.license) {
      return isModuleFree(dataSourceLicense.license);
    }
    if (dataSourceLicense.modules?.length > 0) {
      return dataSourceLicense.modules.every(isModuleFree);
    }
  }
  return false;
}

function isLicensesFree(dataSourceLicenses) {
  return dataSourceLicenses?.every(isLicenseFree);
}

function isLicenseTrial(dataSourceLicense) {
  return dataSourceLicense?.interval === LicenseInterval.Trial;
}

function getDataSourceLicenseTitle(dataSourceLicense) {
  const { license, modules } = dataSourceLicense || {};

  if (license) {
    return license.title;
  }

  if (modules?.length > 0) {
    return modules.map(m => m.title).join(', ');
  }

  return null;
}

function getDataSourceLicenseUniqueTitle(dataSourceLicense) {
  const { license, modules } = dataSourceLicense || {};

  if (license) {
    return license.uniqueTitle || license.title;
  }

  if (modules?.length > 0) {
    return modules.map(m => m.uniqueTitle || m.title).join(', ');
  }

  return null;
}

function getModuleLimitSetting(module, level) {
  if (isModuleFree({ level })) {
    return MODULE_FREE_LIMIT_SETTING[module];
  }

  return MODULE_LIMIT_SETTING[module];
}

function isLimitInPriceOptions(license, setting, limitValue) {
  if (limitValue == null || !license?.settings?.prices?.length) {
    return false;
  }

  const normalizedLimit = parseInt(limitValue, 10);

  if (Number.isNaN(normalizedLimit)) {
    return false;
  }

  return license.settings.prices.some(
    price => price[setting] != null && parseInt(price[setting], 10) === normalizedLimit,
  );
}

function licenseIncludesModule(dataSourceLicense, module) {
  return dataSourceLicense?.modules?.some(m => m.module === module) === true;
}

function hasModuleLicense(dataSourceLicense, module) {
  return dataSourceLicense?.license != null || licenseIncludesModule(dataSourceLicense, module);
}

function hasAccessibilityLicense(dataSourceLicense) {
  return licenseIncludesModule(dataSourceLicense, Module.Accessibility)
    || dataSourceLicense?.settings?.accessibilityEnabled === true;
}

function getLicenseStatus(license) {
  if (license?.endDate && dayjs(license.endDate).isBefore()) {
    return 'ENDED';
  }

  if (license?.startDate && dayjs(license.startDate).isAfter()) {
    return 'UPCOMING';
  }

  return 'ACTIVE';
}

const INTERVAL_DAYS = {
  [LicenseInterval.Yearly]: 365,
  [LicenseInterval.Monthly]: 30,
};

function getLicenseIntervalDays(interval) {
  return INTERVAL_DAYS[interval] || 0;
}

function getLicenseEndDate(startDate, interval, endDate) {
  return dayjs(endDate ?? dayjs(startDate).add(1, interval === LicenseInterval.Yearly ? 'year' : 'month').subtract(1, 'day'))
    .endOf('day')
    .toDate();
}

function getRemainingLicenseDays(activeLicense, startDate) {
  if (!activeLicense?.endDate || !startDate) {
    return 0;
  }

  return dayjs(activeLicense.endDate).diff(dayjs(startDate), 'day') + 1;
}

function isMidTermLicenseChange({ activeLicense, startDate, interval }) {
  if (!activeLicense?.endDate || !startDate || interval === LicenseInterval.Trial || activeLicense.interval === LicenseInterval.Trial) {
    return false;
  }

  return dayjs(startDate).isBefore(dayjs(activeLicense.endDate));
}

function isMidTermUpgradePeriodBlocked({ activeLicense, startDate, interval }) {
  if (!isMidTermLicenseChange({ activeLicense, startDate, interval })) {
    return false;
  }

  const newTermDays = getLicenseIntervalDays(interval);

  if (!newTermDays) {
    return false;
  }

  return getRemainingLicenseDays(activeLicense, startDate) > newTermDays;
}

function settingsToMap(settings) {
  return Object.fromEntries((settings || []).map(({ key, value }) => {
    const parsed = parseInt(value, 10);
    return [key, Number.isNaN(parsed) ? value : parsed];
  }));
}

function isMidTermDowngradeBlocked({
  activeLicense,
  startDate,
  interval,
  modules,
  settings,
  licenses,
  license,
  level,
  allowDowngrade = false,
}) {
  if (allowDowngrade || !isMidTermLicenseChange({ activeLicense, startDate, interval })) {
    return false;
  }

  if (isMidTermUpgradePeriodBlocked({ activeLicense, startDate, interval })) {
    return true;
  }

  const newSettings = settingsToMap(settings);

  if (activeLicense?.license) {
    if (license?.level != null && license.level < activeLicense.license.level) {
      return true;
    }

    const existingVisitLimit = activeLicense.settings?.matomoMonthlyVisitLimit;
    const newVisitLimit = newSettings.matomoMonthlyVisitLimit;

    if (existingVisitLimit != null && newVisitLimit != null && newVisitLimit < existingVisitLimit) {
      return true;
    }

    return false;
  }

  if (!activeLicense?.modules?.length) {
    return false;
  }

  if (level != null) {
    const activeLevel = Math.max(...activeLicense.modules.map(m => m.level));

    if (level < activeLevel) {
      return true;
    }
  }

  const newModuleLicenses = (modules || [])
    .map(id => licenses?.find(l => l.id === id))
    .filter(Boolean);

  for (const activeModule of activeLicense.modules) {
    const newModuleLicense = newModuleLicenses.find(l => l.module === activeModule.module);

    if (!newModuleLicense) {
      return true;
    }

    if (newModuleLicense.level < activeModule.level) {
      return true;
    }

    const limitSetting = getModuleLimitSetting(activeModule.module, activeModule.level);
    const existingLimit = activeLicense.settings?.[limitSetting];
    const newLimit = newSettings[limitSetting];

    if (existingLimit != null && newLimit != null && newLimit < existingLimit) {
      return true;
    }
  }

  return false;
}

function getValidLicenseInterval({ activeLicense, startDate, interval, fallback = LicenseInterval.Yearly }) {
  if (!interval || isMidTermUpgradePeriodBlocked({ activeLicense, startDate, interval })) {
    return fallback;
  }

  return interval;
}

module.exports = {
  hasLicenseLimit,
  formatLicenseLimit,
  reachedLicenseLimit,
  exceededLicenseLimit,
  isModuleFree,
  isLicenseFree,
  isLicensesFree,
  isLicenseTrial,
  getDataSourceLicenseTitle,
  getDataSourceLicenseUniqueTitle,
  getModuleLimitSetting,
  isLimitInPriceOptions,
  hasModuleLicense,
  hasAccessibilityLicense,
  licenseIncludesModule,
  getLicenseStatus,
  getLicenseIntervalDays,
  getLicenseEndDate,
  getRemainingLicenseDays,
  isMidTermLicenseChange,
  isMidTermUpgradePeriodBlocked,
  isMidTermDowngradeBlocked,
  getValidLicenseInterval,
};
